
import { useState, useCallback, useEffect } from 'react';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';

interface AudioSessionTracker {
  hasPlayedAudio: (prayerId: string) => boolean;
  isPausedAudio: (prayerId: string) => boolean;
  markAudioPlayed: (prayerId: string) => void;
  markAudioPaused: (prayerId: string) => void;
  clearSession: () => void;
}

export function useAudioSessionTracking(): AudioSessionTracker {
  const [playedPrayers, setPlayedPrayers] = useState<Set<string>>(new Set());
  const [pausedPrayers, setPausedPrayers] = useState<Set<string>>(new Set());
  const { currentSessionId, isPlaying } = useAbielAudio();

  // Track when audio starts playing for a prayer
  useEffect(() => {
    if (isPlaying && currentSessionId) {
      const prayerMatch = currentSessionId.match(/^prayer_(.+)$/);
      if (prayerMatch) {
        const prayerId = prayerMatch[1];
        markAudioPlayed(prayerId);
        // Remove from paused when playing
        setPausedPrayers(prev => {
          const newSet = new Set([...prev]);
          newSet.delete(prayerId);
          return newSet;
        });
      }
    }
  }, [isPlaying, currentSessionId]);

  // Enhanced paused state tracking
  useEffect(() => {
    if (!isPlaying && currentSessionId) {
      const prayerMatch = currentSessionId.match(/^prayer_(.+)$/);
      if (prayerMatch) {
        const prayerId = prayerMatch[1];
        // Only mark as paused if it was previously played and session is still active
        if (playedPrayers.has(prayerId)) {
          console.log('[AudioSessionTracking] Marking prayer as paused:', prayerId);
          markAudioPaused(prayerId);
        }
      }
    }
  }, [isPlaying, currentSessionId, playedPrayers]);

  // Clear paused state when session ends completely
  useEffect(() => {
    if (!currentSessionId) {
      console.log('[AudioSessionTracking] Session ended, clearing paused states');
      setPausedPrayers(new Set());
    }
  }, [currentSessionId]);

  const hasPlayedAudio = useCallback((prayerId: string) => {
    return playedPrayers.has(prayerId);
  }, [playedPrayers]);

  const isPausedAudio = useCallback((prayerId: string) => {
    const isCurrentlyPaused = pausedPrayers.has(prayerId);
    const isCurrentSession = currentSessionId === `prayer_${prayerId}`;
    
    // Only consider it paused if it's the current session and marked as paused
    return isCurrentlyPaused && isCurrentSession && !isPlaying;
  }, [pausedPrayers, currentSessionId, isPlaying]);

  const markAudioPlayed = useCallback((prayerId: string) => {
    setPlayedPrayers(prev => new Set([...prev, prayerId]));
  }, []);

  const markAudioPaused = useCallback((prayerId: string) => {
    setPausedPrayers(prev => new Set([...prev, prayerId]));
  }, []);

  const clearSession = useCallback(() => {
    setPlayedPrayers(new Set());
    setPausedPrayers(new Set());
  }, []);

  return {
    hasPlayedAudio,
    isPausedAudio,
    markAudioPlayed,
    markAudioPaused,
    clearSession
  };
}
